// Lightweight tenant WS listener for `callback.missed` alerts.
// Mounted in the Admin console (which does NOT mount the workspace
// softphone hook), so admins/supervisors get the missed-callback toast
// even when they aren't on the agent workspace / live dashboard.
import { useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const RAW_BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const WS_BASE = RAW_BACKEND.replace(/^http/, "ws");

export function useMissedCallbackAlerts() {
  const { user } = useAuth();
  useEffect(() => {
    if (!user) return;
    const role = user.role || "";
    const isSup = ["supervisor", "admin", "super_admin", "super_super_admin"].includes(role);
    if (!isSup) return; // only supervisors/admins get the ops alert here
    const token = localStorage.getItem("c7_token");
    if (!token) return;
    let stopped = false;
    let reconnect = null;
    let ws = null;

    function connect() {
      if (stopped) return;
      ws = new WebSocket(`${WS_BASE}/api/ws/agent?token=${encodeURIComponent(token)}`);
      ws.onmessage = (e) => {
        if (e.data === "pong") return;
        let msg; try { msg = JSON.parse(e.data); } catch { return; }
        if (msg.type === "callback.missed") {
          const who = msg.customer || msg.phone || "a customer";
          const why = msg.reason === "no_active_trunk" ? "no active trunk" : "no agent was available";
          try {
            toast.warning(`Missed callback: ${who} — ${why} at the scheduled time`, { duration: 10000 });
          } catch {}
        }
      };
      ws.onclose = () => { if (!stopped) reconnect = setTimeout(connect, 2500); };
      ws.onerror = () => { try { ws.close(); } catch {} };
    }
    connect();
    return () => {
      stopped = true;
      if (reconnect) clearTimeout(reconnect);
      try { ws?.close(); } catch {}
    };
  }, [user?.id]);
}
